/**
 * The intake form.
 *
 * Structure comes from lib/formSchema.js, requirement levels from Firestore
 * (`settings/fieldRules`) via lib/fieldRules.js, and conditional reveal from
 * lib/conditions.js. Nothing about "which fields exist" or "which are required" is
 * hardcoded here.
 *
 * Drafts autosave to `matters/{id}` with status 'draft' every few seconds, so a
 * half-finished intake survives a closed tab. Submission hands off to the
 * `submitMatter` callable, which builds the approval chain server-side.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { deleteField, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../auth/AuthProvider';
import Attachments from '../components/Attachments';
import ConsistencyReviewPanel from '../components/ConsistencyReviewPanel';
import Field from '../components/Field';
import SharedWithCard from '../components/SharedWithCard';
import { evaluateCondition, getPath, setPath } from '../lib/conditions';
import {
  applyInitialFieldDefaults,
  deriveMatter,
  hiddenConditionalFieldPaths,
  pruneUndefined,
  withCreatedAt,
} from '../lib/deriveMatter';
import { mergeAttachmentCategory } from '../lib/attachments';
import { fieldGridClasses } from '../lib/fieldGrid';
import { bypassReasonProblem } from '../lib/bypassReason';
import {
  REQUIREMENT,
  blockingBannerText,
  isLiveIncomplete,
  isSectionComplete,
  makeRulesEngine,
  toBypassPayload,
  visibleSections,
} from '../lib/fieldRules';
import {
  buildBuilderSections,
  buildEffectiveSections,
  clonePolicyFieldList,
  effectiveFieldList,
  formatFieldValueForDisplay,
  stripLifecycleFields,
  stripPreventCloneFields,
} from '../lib/fieldLayout';
import {
  createDraft,
  deleteField as deleteFieldFromLayout,
  describeSection,
  setSectionCondition,
  moveFieldToSection,
  moveFieldWithinSection,
  patchField,
  renameSection,
  toSavePayload,
} from '../lib/layoutEditor';
import { buildDeleteWarnings } from '../lib/conditionPaths';
import { FieldEditShell, LayoutEditBar } from '../components/LayoutEditor';
import ConditionBuilder from '../components/ConditionBuilder';
import { conditionBuilderFieldList } from '../lib/fieldLayout';
import PracticeCoach from '../components/PracticeCoach';
import { GUIDANCE } from '../lib/walkthroughSteps';
import { guidanceForField } from '../lib/fieldGuidance';
import { canAdvanceFromSection, canNavigateToSection } from '../lib/navigationGate';
import { isConsistencyReviewEnabled, isSharingEnabled, submitButtonLabel } from '../lib/matterRecordPdf';
import { FORM_SECTIONS, emptyMatter } from '../lib/formSchema';
import { useCallable } from '../lib/useCallable';
import { useCollection, useDoc, useFormConfig } from '../lib/useFirestore';

const AUTOSAVE_MS = 2500;

/**
 * @param {object}  props
 * @param {boolean} [props.practice]  A guided practice run on the real form.
 *
 * PRACTICE MODE'S ONE GUARANTEE IS THAT NOTHING PERSISTS, and it is enforced here rather than in
 * the coaching panel, because the guarantee must not depend on a piece of UI behaving itself.
 * There are exactly three ways this component writes anything — the debounced autosave, the
 * "Save draft" button, and submit — and all three funnel through `save()` and `submit()`, which
 * both return immediately in practice mode. Inline layout editing is refused for the same reason:
 * it writes settings, which are firm-wide.
 */
export default function IntakeForm({ practice = false }) {
  const { matterId: routeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const config = useFormConfig();

  // "Clone this matter" and "Start from template" both hand the pre-filled data over in
  // router state. It is read once, when the blank draft is initialised.
  const seedMatter = location.state?.seedMatter || null;
  const seedLabel = location.state?.seedLabel || '';

  const [matterId] = useState(() => routeId || doc(db, 'matters', crypto.randomUUID()).id);
  const [matter, setMatter] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const [saveState, setSaveState] = useState('idle');
  const [bypasses, setBypasses] = useState({});
  const [showProblems, setShowProblems] = useState(false);
  const [submitError, setSubmitError] = useState('');
  /**
   * Which action the submitter last attempted while something was still blocking
   * ('submit' | 'navigate' | null). It selects the WORDING of the banner; whether the
   * banner shows at all is derived from the live problem set below, so it clears itself the
   * moment the last problem is fixed instead of surviving until the next submit.
   */
  const [blockedIntent, setBlockedIntent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [consistencyReview, setConsistencyReview] = useState(null);
  const [consistencyReviewBusy, setConsistencyReviewBusy] = useState(false);
  const [consistencyReviewError, setConsistencyReviewError] = useState('');
  const [pendingSubmitAfterReview, setPendingSubmitAfterReview] = useState(false);
  const [verifiedPaths, setVerifiedPaths] = useState({});
  const [expandedSections, setExpandedSections] = useState(() => new Set());
  // Tracks whether matters/{matterId} has actually been written yet, independent of the
  // URL: routeId is only set for an already-saved draft opened via /matters/:matterId/edit,
  // but a brand-new matter (matters/new) gets its first Firestore write from the debounced
  // autosave below without the URL ever updating to reflect it. See the save() callback.
  const [docExists, setDocExists] = useState(Boolean(routeId));
  const dirty = useRef(false);

  // SharedWithCard needs to resolve names for collaborators even if they've since gone
  // inactive (see roster.find fallback there), so — same as MatterDetail.jsx — this is an
  // unfiltered roster pull, not config.roster (which is pre-filtered to isActive users only).
  const roster = useCollection('roster');

  // Load an existing draft, or start a new one.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (routeId) {
        const snap = await getDoc(doc(db, 'matters', routeId));
        if (!cancelled) setMatter(snap.exists() ? snap.data() : emptyMatter(user.uid));
      } else if (!cancelled) {
        const blank = emptyMatter(user.uid);
        setMatter(seedMatter ? { ...blank, ...seedMatter } : blank);
        if (seedMatter) dirty.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
    // seedMatter is read once at initialisation; later navigations remount this component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, user.uid]);

  /**
   * Per-field starting values (settings/fieldRules → `defaultValue`).
   *
   * Separate from the load effect above because the config arrives asynchronously and is usually
   * still loading when the blank matter is created, so applying them there would silently skip
   * every default. Guarded four ways so it can only ever fire into an untouched new form: once
   * per mount, never for an existing matter, never before the config has loaded, and never after
   * the user has typed anything — including a matter seeded from a template, whose stored answers
   * ARE its starting values.
   *
   * deriveMatter handles the other half: a field revealed later gets its default as it appears.
   */
  const defaultsApplied = useRef(false);
  useEffect(() => {
    if (defaultsApplied.current) return;
    if (routeId || docExists || dirty.current) return;
    if (!matter || config.loading) return;
    defaultsApplied.current = true;
    const seeded = applyInitialFieldDefaults(matter, config);
    if (seeded !== matter) setMatter(seeded);
  }, [matter, config, routeId, docExists]);

  const save = useCallback(
    async (next) => {
      // The choke point. Autosave and the "Save draft" button both arrive here, so one guard
      // covers every path that could leave a document behind.
      if (practice) return;
      // Resolves true once the draft is in Firestore, false if the write failed. Submit needs
      // to know: submitMatter reads the stored document, not this form. (A practice run never
      // submits, so its undefined above is never read.)
      setSaveState('saving');
      try {
        let payload = pruneUndefined(
          withCreatedAt(
            {
              ...next,
              createdBy: user.uid,
              status: 'draft',
              updatedAt: serverTimestamp(),
            },
            serverTimestamp
          )
        );
        // setDoc(..., { merge: true }) preserves an earlier nested value when it is
        // simply omitted from `payload`. Derivation removes hidden answers from React
        // state; write explicit delete sentinels too so hidden conditional answers
        // cannot remain in Firestore or leak into downstream documents.
        for (const path of hiddenConditionalFieldPaths(next, config)) {
          payload = setPath(payload, path, deleteField());
        }
        await setDoc(doc(db, 'matters', matterId), payload, { merge: true });
        setSaveState('saved');
        setDocExists(true);
        return true;
      } catch (err) {
        console.error('[intake] draft save failed', err);
        setSaveState('error');
        return false;
      }
    },
    [config, matterId, user.uid, practice]
  );

  // Debounced autosave. Skipped entirely in practice mode rather than relying on save()'s guard,
  // so a practice run does not sit there firing a timer every few seconds to do nothing.
  useEffect(() => {
    if (practice) return undefined;
    if (!matter || !dirty.current || matter.status !== 'draft') return undefined;
    const timer = setTimeout(() => {
      dirty.current = false;
      save(matter);
    }, AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [matter, save, practice]);

  const update = (next) => {
    dirty.current = true;
    setSaveState('unsaved');
    setMatter(deriveMatter(matter, next, config));
  };

  // Inline layout editing (admin only): while a draft is open, the form renders from the
  // DRAFT layout — including hidden fields and pages — so every edit previews live. The
  // draft is saved through the same adminUpdateSettings callable Admin → Field Builder
  // uses; until then Firestore is untouched. See lib/layoutEditor.js.
  const layoutCallable = useCallable();
  const [layoutDraft, setLayoutDraft] = useState(null);
  const [layoutDirty, setLayoutDirty] = useState(false);
  // Never in a practice run: layout editing writes settings/fieldLayout, which is firm-wide, and
  // a tour is the last place anybody should discover they have just republished the form.
  const editingLayout = isAdmin && !practice && layoutDraft !== null;

  /**
   * What the coaching panel says about a page.
   *
   * Shared with the Help page's written list (lib/walkthroughSteps.js), so there is one
   * description of each page rather than two that drift. A page the firm invented has no entry,
   * and gets its own description — which is what whoever built it wrote for this purpose.
   */
  const practiceGuidanceFor = useCallback(
    (sectionToDescribe) => {
      const known = GUIDANCE[sectionToDescribe.id];
      if (known) {
        return (known.body || [])
          .map((line) => (line && line.__approvalsOnly ? '' : line))
          .filter(Boolean)
          .concat(known.tip ? [known.tip] : []);
      }
      return sectionToDescribe.description ? [sectionToDescribe.description] : [];
    },
    []
  );
  // Only subscribed while editing: used to NAME the approval steps a field deletion would
  // affect, so removing a governance-referenced field is a deliberate act, never silent.
  // The loading flag gates the Delete confirm — an admin must never see an empty warning
  // list merely because the flow document hadn't arrived yet.
  const { data: approvalFlowDoc, loading: approvalFlowLoading } = useDoc(
    editingLayout ? 'settings/approvalFlow' : ''
  );

  // Code defaults + the admin's settings/fieldLayout overrides. With no layout document
  // this is byte-for-byte FORM_SECTIONS, so an uncustomised firm sees today's form.
  const effectiveSections = useMemo(
    () => buildEffectiveSections(FORM_SECTIONS, config.fieldLayout),
    [config.fieldLayout]
  );
  const builderSections = useMemo(
    () => (layoutDraft ? buildBuilderSections(FORM_SECTIONS, layoutDraft) : null),
    [layoutDraft]
  );
  // Field list for the page-visibility condition editor, derived from the live draft so
  // custom fields and admin-renamed labels are pickable. Only computed while editing.
  const conditionFields = useMemo(
    () => (layoutDraft ? conditionBuilderFieldList(FORM_SECTIONS, layoutDraft) : []),
    [layoutDraft]
  );
  const effectiveFields = useMemo(
    () => effectiveFieldList(effectiveSections),
    [effectiveSections]
  );

  const engine = useMemo(
    () =>
      makeRulesEngine(config.fieldRules, {
        sections: effectiveSections,
        allFields: effectiveFields,
        // Lets a structural warning name a person ("Bob has no origination share…")
        // instead of printing a raw account id.
        roster: config.roster,
        // The per-attorney origination cap, so the client flags an over-cap row instead of
        // letting submitMatter be the first thing that mentions it.
        originationCapPercent: config.general?.originationCapPercent,
      }),
    [
      config.fieldRules,
      effectiveSections,
      effectiveFields,
      config.roster,
      config.general?.originationCapPercent,
    ]
  );
  // In edit mode, show EVERY page and field (hidden ones dimmed) regardless of
  // conditional visibility, so an admin can edit litigation fields without first
  // faking a litigation matter.
  const sections = useMemo(
    () =>
      editingLayout && builderSections
        ? builderSections
        : matter
          ? visibleSections(matter, effectiveSections)
          : [],
    [editingLayout, builderSections, matter, effectiveSections]
  );

  /**
   * Which question the practice run is explaining, if any.
   *
   * Set by a click anywhere inside a field — the wrapper carries `data-field-path`, and
   * `closest()` finds it however deep the click landed. Cleared by a click on the background,
   * which is why the handler is on the whole form rather than on each field: a per-field handler
   * would have no way to know the person had clicked away.
   */
  const [explainingPath, setExplainingPath] = useState(null);

  const practiceFieldGuidance = useMemo(() => {
    if (!practice || !explainingPath) return null;
    const field = sections
      .flatMap((s) => s.fields || [])
      .find((f) => f.path === explainingPath);
    return field ? guidanceForField(field, config) : null;
  }, [practice, explainingPath, sections, config]);

  const onPracticeClick = useCallback(
    (event) => {
      if (!practice) return;
      const wrapper = event.target.closest?.('[data-field-path]');
      setExplainingPath(wrapper ? wrapper.getAttribute('data-field-path') : null);
    },
    [practice]
  );

  const validation = useMemo(
    () => (matter ? engine.validate(matter, bypasses) : null),
    [engine, matter, bypasses]
  );

  // A default-collapsed section must never conceal a problem after a submit attempt.
  // The Set is intentionally session-local: once someone opens a qualifying section,
  // switching between sections or editing other answers cannot auto-collapse it again.
  useEffect(() => {
    if (!showProblems || !matter || !validation) return;
    const problemSectionIds = new Set(
      [...validation.errors, ...validation.warnings].map((problem) => problem.sectionId)
    );
    const problemSection = sections.find(
      (entry) =>
        entry.defaultCollapsedIf &&
        evaluateCondition(entry.defaultCollapsedIf, matter) &&
        problemSectionIds.has(entry.id)
    );
    if (!problemSection || expandedSections.has(problemSection.id)) return;
    setExpandedSections((current) => new Set([...current, problemSection.id]));
  }, [expandedSections, matter, sections, showProblems, validation]);

  // Every visible field an admin flagged "Verification required" in the Field Builder
  // (see lib/fieldLayout.js's verificationRequired flag) — this is the checklist the
  // pre-submit modal shows. "Visible" mirrors what the submitter can actually see:
  // fields hidden by conditional logic are not shown in `sections`. Declared here,
  // before the early returns below, so this hook always runs on every render — a
  // useMemo declared after a conditional `return` only runs once `matter` exists,
  // which changes the hook count between the loading render and the loaded render
  // and throws React error #310 ("Rendered fewer hooks than expected").
  const fieldsToVerify = useMemo(
    () => sections.flatMap((s) => s.fields).filter((f) => f.path && f.verificationRequired === true),
    [sections]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // NO HOOKS BELOW THIS LINE. Everything past here runs only once `matter` exists, so a
  // useState/useMemo/useEffect/useCallback added below changes the hook count between the
  // loading render and the loaded render and crashes the page with React error #310. This
  // has now happened twice. If a value below needs memoizing, either hoist the hook above
  // this boundary or — better — move the computation into a pure function in lib/ and call
  // it (see blockingBannerText).
  // ─────────────────────────────────────────────────────────────────────────────
  if (!matter || config.loading) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">Loading form…</p>;
  }

  // Surface a config load failure (e.g. a permission or index error on one of the
  // settings/roster/practiceGroups listeners) instead of silently rendering the form
  // with partial/null config, which previously could make the page look "stuck" with
  // no visible explanation — indistinguishable from a broken Save button.
  if (config.error) {
    return (
      <p className="text-sm text-red-600 dark:text-red-400">
        Couldn&apos;t load the form configuration ({config.error.message || String(config.error)}).
        Refresh the page, or contact IT if this keeps happening.
      </p>
    );
  }

  const section = sections[Math.min(activeSection, sections.length - 1)];
  const sectionIsCollapsed =
    !editingLayout &&
    section?.defaultCollapsedIf &&
    evaluateCondition(section.defaultCollapsedIf, matter) &&
    !expandedSections.has(section.id);

  // ── Layout edit mode handlers (all state transitions live in lib/layoutEditor.js) ──
  const startLayoutEdit = () => {
    setLayoutDraft(createDraft(config.fieldLayout));
    setLayoutDirty(false);
  };
  const exitLayoutEdit = () => {
    setLayoutDraft(null);
    setLayoutDirty(false);
  };
  const patchLayoutField = (field, patch) => {
    setLayoutDraft((draft) => patchField(draft, field, patch));
    setLayoutDirty(true);
  };
  const moveLayoutField = (sectionFields, field, direction) => {
    setLayoutDraft((draft) => moveFieldWithinSection(draft, sectionFields, field, direction));
    setLayoutDirty(true);
  };
  const moveLayoutFieldToSection = (field, targetSectionId) => {
    const target = sections.find((s) => s.id === targetSectionId);
    setLayoutDraft((draft) => moveFieldToSection(draft, field, targetSectionId, target?.fields || []));
    setLayoutDirty(true);
  };
  const deleteLayoutField = (field) => {
    setLayoutDraft((draft) => deleteFieldFromLayout(draft, field));
    setLayoutDirty(true);
  };
  // Named consequences shown in the delete confirmation — computed against the LIVE
  // approval flow and the MERGED sections (so admin-authored conditions and custom
  // fields count as dependents). See lib/conditionPaths.js buildDeleteWarnings.
  const deleteWarningsFor = (field) =>
    buildDeleteWarnings(field, { flow: approvalFlowDoc, sections });
  const renameLayoutSection = (id, title) => {
    setLayoutDraft((draft) => renameSection(draft, id, title));
    setLayoutDirty(true);
  };
  const setLayoutSectionCondition = (id, condition) => {
    setLayoutDraft((draft) => setSectionCondition(draft, id, condition));
    setLayoutDirty(true);
  };
  const describeLayoutSection = (id, description) => {
    setLayoutDraft((draft) => describeSection(draft, id, description));
    setLayoutDirty(true);
  };
  const saveLayout = async () => {
    const result = await layoutCallable.run('adminUpdateSettings', {
      docId: 'fieldLayout',
      value: toSavePayload(layoutDraft, config.fieldLayout?.version),
    });
    if (result !== null) exitLayoutEdit();
  };
  const problemsByPath = Object.fromEntries(
    [...(validation?.errors || []), ...(validation?.warnings || [])].map((p) => [p.path, p])
  );
  const isOwner = matter.createdBy === user.uid;
  const workflowEnabled = config.general?.workflowEnabled !== false;
  const sharingEnabled = isSharingEnabled(config.general);
  const submitLabel = submitButtonLabel(config.general, { workflowEnabled });
  const consistencyReviewEnabled = isConsistencyReviewEnabled(config.general);
  // An absent setting keeps v1.8.2's page-completeness gate enabled.
  // Layout editing must be able to reach every page regardless of form completeness —
  // an admin restyling the form is not filling it in, so the page gate is suspended.
  const pageGateDisabled = config.general?.pageGateDisabled === true || editingLayout;
  /**
   * The blocking-problems banner. Derived, not latched: it names the pages holding the
   * problems and disappears as soon as the last one is fixed.
   */
  // NOT a useMemo: this is below the early returns, where a hook would change the hook count
  // between the loading render and the loaded render (React error #310). It is a plain call
  // into lib/fieldRules.js, which is also where it is tested.
  const completedSections = sections.filter((section) =>
    isSectionComplete(validation?.bySection[section.id] || [])
  ).length;
  const completedPercent = sections.length
    ? Math.round((completedSections / sections.length) * 100)
    : 0;

  const blockingBanner = blockingBannerText(validation, {
    intent: blockedIntent,
    sections,
    sectionId: section?.id,
  });

  const currentSectionComplete = canAdvanceFromSection(
    section,
    validation?.bySection,
    pageGateDisabled
  );

  // This uses the same showProblems state that submit uses, so a navigation gate makes the
  // current field-level errors and existing summary visible rather than introducing a second
  // error-display convention.
  const showNavigationProblems = () => {
    setShowProblems(true);
    setSubmitError('');
    setBlockedIntent('navigate');
  };

  const doSubmit = async () => {
    setSubmitting(true);
    try {
      // Submitting after a failed save would file the last autosaved copy, silently missing
      // whatever was typed since. Stop instead, while the answers are still on screen.
      if (!(await save(matter))) {
        setSubmitError(
          'Your latest changes could not be saved, so nothing was submitted. Check your connection and click submit again.'
        );
        return;
      }
      const call = httpsCallable(functions, 'submitMatter');
      // From softRequired, not warnings: a waived field is absent from warnings by the time
      // anyone submits, so harvesting labels there sent none — and the matter record printed
      // the raw path "client.invoiceEmail" instead of "Invoice Email Address".
      const bypassLabels = Object.fromEntries(
        validation.softRequired
          .concat(validation.warnings, validation.errors)
          .map((w) => [w.path, w.label])
      );
      await call({ matterId, softRequiredBypasses: toBypassPayload(bypasses, bypassLabels) });
      navigate(`/matters/${matterId}`);
    } catch (err) {
      setSubmitError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const runConsistencyReview = async ({ beforeSubmit = false } = {}) => {
    setConsistencyReviewBusy(true);
    setConsistencyReviewError('');
    setConsistencyReview(null);
    try {
      const call = httpsCallable(functions, 'reviewMatterConsistency');
      const result = await call({ matter });
      const review = result.data;
      setConsistencyReview(review);
      if (beforeSubmit && review.issues?.length) {
        setPendingSubmitAfterReview(true);
        return false;
      }
      return true;
    } catch (err) {
      setConsistencyReviewError(err.message || String(err));
      return true; // Informational review must never block a valid submission.
    } finally {
      setConsistencyReviewBusy(false);
    }
  };

  const openVerifyModal = () => {
    setVerifiedPaths({});
    setVerifyModalOpen(true);
  };

  const submit = async () => {
    // Nothing to submit in a practice run, and the callable would create a real matter.
    if (practice) {
      setSubmitError(
        'This is a practice run, so there is nothing to submit. Start a real one from New matter in the left rail.'
      );
      return;
    }
    setSubmitError('');
    setBlockedIntent(null);
    setShowProblems(true);
    if (!validation.canSubmit) {
      const firstProblem = [...validation.errors, ...validation.warnings][0];
      if (firstProblem) {
        const firstSection = sections.findIndex((s) => s.id === firstProblem.sectionId);
        if (firstSection >= 0) setActiveSection(firstSection);
      }
      setBlockedIntent('submit');
      return;
    }

    // Save the current draft first so previewApprovalChain (called by the modal below)
    // has a persisted matters/{matterId} doc to read -- it authorizes against
    // matter.createdBy and needs real field values to evaluate the chain's trigger
    // conditions (e.g. GC's isNewClient/practiceGroupId checks).
    await save(matter);

    // Always open the Verify modal now, even when no field is flagged
    // "Verification required" (fieldsToVerify.length === 0) -- it doubles as the
    // pre-submit "who will approve this" preview, which is worth showing on every
    // submission regardless of whether there is also a verification checklist.
    const mayContinue = consistencyReviewEnabled
      ? await runConsistencyReview({ beforeSubmit: true })
      : true;
    if (mayContinue) openVerifyModal();
  };

  return (
    <div
      className={`grid gap-7 lg:grid-cols-[292px_1fr] ${practice ? 'pb-40' : ''}`}
      onClickCapture={onPracticeClick}
    >
      {/*
        The practice run's two pieces: a band across the top that cannot be missed or scrolled
        past, and the coaching panel. The band is not the coach — somebody who collapses the guide
        must still be able to see, at a glance, that this is not a real matter.
      */}
      {practice && (
        <>
          <div className="lg:col-span-2 rounded-lg border border-accent-500 bg-accent-50 px-4 py-2.5 text-sm text-slate-800 dark:bg-slate-800 dark:text-slate-100">
            <strong>Practice run.</strong> This is the real form, but nothing is saved — no draft
            is created and nothing is submitted. Everything you type here disappears when you
            leave.
          </div>
          <PracticeCoach
            sections={sections}
            activeSection={activeSection}
            onGoToSection={setActiveSection}
            guidanceFor={practiceGuidanceFor}
            fieldGuidance={practiceFieldGuidance}
            onClearField={() => setExplainingPath(null)}
            onExit={() => navigate('/help')}
          />
        </>
      )}
      <div className="flex min-h-full flex-col border-r border-slate-200 pr-1 dark:border-slate-700">
        <p className="px-[22px] pb-3 text-[11.5px] font-semibold uppercase tracking-[.14em] text-slate-500 dark:text-slate-400">
          Intake sections
        </p>
        <nav>
          {sections.map((s, index) => {
            const sectionProblems = validation?.bySection[s.id] || [];
            const count = sectionProblems.length;
            // Live (not gated by showProblems/submit-attempt) — see isSectionComplete.
            const sectionComplete = isSectionComplete(sectionProblems);
            const isCurrent = index === activeSection;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  if (
                    !canNavigateToSection({
                      currentIndex: activeSection,
                      targetIndex: index,
                      sections,
                      bySection: validation?.bySection,
                      pageGateDisabled,
                    })
                  ) {
                    showNavigationProblems();
                    return;
                  }
                  setActiveSection(index);
                }}
                className={`relative flex w-full items-center gap-3 px-[22px] py-3 text-left text-[14.5px] ${
                  isCurrent
                    ? 'bg-accent-50 font-medium text-firm-700 dark:bg-firm-900 dark:text-firm-100'
                    : 'text-ink hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'
                }`}
              >
                {isCurrent && (
                  <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-accent-500" />
                )}
                {/* A plain circle: filled once the section is complete, hollow until then, with a
                    green ring on the one you are on. The number is gone because it never carried
                    anything — the sections are already in order down the column, and the row itself
                    marks the current one with a green rule, a tint and a heavier label. */}
                <span
                  aria-hidden="true"
                  className={`h-[12px] w-[12px] shrink-0 rounded-full ${
                    sectionComplete
                      ? 'bg-accent-500'
                      : isCurrent
                        ? 'ring-[2px] ring-accent-500'
                        : 'ring-[1.5px] ring-slate-300 dark:ring-slate-600'
                  }`}
                />
                {/* The circle is decorative, so completion is stated for screen readers instead. */}
                {sectionComplete && <span className="sr-only">Complete: </span>}
                <span className="min-w-0 flex-1 truncate">{s.title}</span>
                {showProblems && count > 0 && (
                  <span className="ml-1 shrink-0 rounded-full bg-red-100 px-1.5 text-xs text-red-600 dark:bg-red-950 dark:text-red-300">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Pinned to the bottom: the count, the percentage, and a track — the same fact three ways,
            because a fraction, a percentage and a bar are each quicker to read for different people. */}
        <div className="sticky bottom-0 mt-auto bg-slate-50 px-[22px] pb-1 pt-4 dark:bg-slate-950">
          <div className="flex items-baseline justify-between text-[13px] text-ink dark:text-slate-300">
            <span>
              {completedSections} of {sections.length} sections complete
            </span>
            <span className="font-medium">{completedPercent}%</span>
          </div>
          <div className="mt-2 h-[5px] overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
            <div
              className="h-full rounded-full bg-accent-500 transition-[width]"
              style={{ width: `${completedPercent}%` }}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-end gap-3">
          {isAdmin && !editingLayout && (
            <button
              type="button"
              onClick={startLayoutEdit}
              className="rounded border border-slate-300 px-3 py-1 text-xs text-firm-700 hover:bg-slate-100 dark:border-slate-600 dark:text-firm-100 dark:hover:bg-slate-800"
              title="Rename, resize, move, hide or delete fields directly on the form (admins only)"
            >
              Edit layout
            </button>
          )}
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {{ idle: '', unsaved: 'Unsaved changes…', saving: 'Saving…', saved: 'Draft saved', error: 'Save failed' }[saveState]}
          </span>
        </div>

        {editingLayout && (
          <LayoutEditBar
            dirty={layoutDirty}
            busy={layoutCallable.busy}
            error={layoutCallable.error}
            onSave={saveLayout}
            onDiscard={exitLayoutEdit}
          />
        )}

        {seedLabel && !routeId && (
          <p className="rounded border border-firm-100 dark:border-firm-700 bg-firm-50 dark:bg-firm-900 p-3 text-sm text-firm-700 dark:text-firm-100">
            {seedLabel} Review every field before submitting — nothing has been submitted yet.
          </p>
        )}

        <section className="rounded-lg bg-white dark:bg-slate-900 p-6 shadow">
          {editingLayout ? (
            <>
              <div className="flex items-center gap-2">
                <input
                  className="w-full max-w-md rounded border border-dashed border-firm-300 px-2 py-1 text-lg font-medium text-firm-700 dark:border-firm-700 dark:bg-slate-900 dark:text-firm-100"
                  value={section.title}
                  onChange={(event) => renameLayoutSection(section.id, event.target.value)}
                  aria-label={`Title for page ${section.id}`}
                />
                {section.isActive === false && (
                  <span className="rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                    hidden page
                  </span>
                )}
              </div>
              <textarea
                className="mt-1 w-full rounded border border-dashed border-firm-300 px-2 py-1 text-sm text-slate-600 dark:border-firm-700 dark:bg-slate-900 dark:text-slate-300"
                rows={2}
                value={section.description || ''}
                placeholder="Page description shown under the title (optional)"
                onChange={(event) => describeLayoutSection(section.id, event.target.value)}
                aria-label={`Description for page ${section.id}`}
              />
              <details className="mt-1 rounded border border-dashed border-firm-300 p-2 text-xs dark:border-firm-700">
                <summary className="cursor-pointer text-slate-600 dark:text-slate-300">
                  Page visibility
                  {section.visibleIf !== undefined && section.visibleIf !== true && (
                    <span className="ml-1 rounded bg-firm-50 px-1 text-firm-800 dark:bg-firm-900 dark:text-firm-100">
                      conditional
                    </span>
                  )}
                </summary>
                <p className="mt-1 text-slate-500 dark:text-slate-400">
                  Show this page only when the condition is met. Empty means always show.
                  A condition set here replaces any rule the page shipped with.
                </p>
                <div className="mt-2">
                  <ConditionBuilder
                    value={section.visibleIf}
                    onChange={(visibleIf) => setLayoutSectionCondition(section.id, visibleIf)}
                    fields={conditionFields}
                    practiceGroups={config.practiceGroups || []}
                    areasOfLaw={(config.areasOfLaw?.items || []).filter((i) => i.isActive !== false)}
                  />
                </div>
                <div className="mt-1 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setLayoutSectionCondition(section.id, true)}
                    className="text-firm-700 hover:underline dark:text-firm-100"
                  >
                    Always show this page
                  </button>
                  <button
                    type="button"
                    onClick={() => setLayoutSectionCondition(section.id, undefined)}
                    className="text-slate-500 hover:underline dark:text-slate-400"
                  >
                    Reset to default
                  </button>
                </div>
              </details>
            </>
          ) : (
            <>
              <h2 className="text-lg font-medium text-firm-700 dark:text-firm-100">{section.title}</h2>
              {section.description && (
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{section.description}</p>
              )}
            </>
          )}

          {sectionIsCollapsed ? (
            <button
              type="button"
              onClick={() =>
                setExpandedSections((current) => new Set([...current, section.id]))
              }
              className="mt-5 w-full rounded border border-slate-300 dark:border-slate-600 px-4 py-3 text-left text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-expanded="false"
            >
              {section.id === 'conflictCheck' ? 'Conflict Check (required)' : section.title} — click to
              expand
            </button>
          ) : (
            <div className="mt-5 grid gap-x-7 gap-y-[22px] sm:grid-cols-6">
              {section.fields.map((raw, fieldIndex) => {
                const field = raw.path ? engine.decorate(raw) : raw;
                const itemKey = `field-${field.path || field.text || fieldIndex}`;
                let control;
                if (field.type === 'attachments') {
                  const category = field.category || 'other';
                  const allAttachments = Array.isArray(matter.attachments) ? matter.attachments : [];
                  const categoryFiles = allAttachments.filter(
                    (f) => (f.category || 'other') === category
                  );
                  control = (
                    <>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                        {field.label}
                      </label>
                      {field.help && <p className="mb-1 text-xs text-slate-500 dark:text-slate-400">{field.help}</p>}
                      <Attachments
                        matterId={matterId}
                        category={category}
                        value={categoryFiles}
                        documentsOnly={field.engagementLetterInline === true}
                        onChange={(next) => {
                          // next is the full replacement list for THIS category only; merge it
                          // back with the other categories' files so they aren't clobbered.
                          update(
                            setPath(
                              matter,
                              'attachments',
                              mergeAttachmentCategory(allAttachments, category, next)
                            )
                          );
                        }}
                      />
                    </>
                  );
                } else {
                  control = (
                    <Field
                      field={field}
                      matter={matter}
                      onChange={update}
                      config={config}
                      problem={!editingLayout && showProblems ? problemsByPath[field.path] : null}
                      liveIncomplete={
                        !editingLayout && isLiveIncomplete(field, getPath(matter, field.path), matter)
                      }
                    />
                  );
                }
                if (editingLayout) {
                  return (
                    <FieldEditShell
                      key={itemKey}
                      field={field}
                      sectionId={section.id}
                      allSections={sections}
                      onPatch={(patch) => patchLayoutField(field, patch)}
                      onMove={(direction) => moveLayoutField(section.fields, field, direction)}
                      onMoveToSection={(targetId) => moveLayoutFieldToSection(field, targetId)}
                      onDelete={() => deleteLayoutField(field)}
                      deleteWarnings={deleteWarningsFor(field)}
                      deleteDisabled={approvalFlowLoading}
                    >
                      {control}
                    </FieldEditShell>
                  );
                }
                if (field.type === 'attachments') {
                  return (
                    <div key={itemKey} className={fieldGridClasses(field)}>
                      {control}
                    </div>
                  );
                }
                /*
                  `data-field-path` is how the practice run knows which question you clicked.
                  `display: contents` generates no box, so this adds nothing to the layout — but
                  the element is still in the DOM, so a click inside the field finds it with
                  closest(). It is on every field, not just in practice mode, because a wrapper
                  that exists only sometimes is a wrapper whose absence nobody notices until it
                  matters.
                */
                return (
                  <div key={itemKey} className="contents" data-field-path={field.path}>
                    {control}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/*
          Gated on softRequired, not warnings: a waived row must stay on screen. Gating on
          warnings is what made the panel vanish mid-sentence.
        */}
        {showProblems && validation.softRequired.length > 0 && (
          <SoftRequiredPanel
            softRequired={validation.softRequired}
            bypasses={bypasses}
            setBypasses={setBypasses}
            reasonRules={config.fieldRules?.bypassReasons}
          />
        )}

        {showProblems && validation.errors.length > 0 && (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-4">
            <h3 className="text-sm font-semibold text-red-800 dark:text-red-200">
              {validation.errors.length} required field
              {validation.errors.length === 1 ? '' : 's'} still to complete
            </h3>
            <ul className="mt-2 space-y-0.5 text-sm text-red-700 dark:text-red-300">
              {validation.errors.map((e) => (
                <li key={e.path}>
                  <button
                    type="button"
                    className="hover:underline"
                    onClick={() =>
                      setActiveSection(Math.max(0, sections.findIndex((s) => s.id === e.sectionId)))
                    }
                  >
                    {e.sectionTitle || e.sectionId} → {e.message}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {blockingBanner && (
          <p className="rounded bg-red-50 dark:bg-red-950 p-3 text-sm text-red-700 dark:text-red-300">
            {blockingBanner}
          </p>
        )}

        {submitError && (
          <p className="rounded bg-red-50 dark:bg-red-950 p-3 text-sm text-red-700 dark:text-red-300">{submitError}</p>
        )}

        <ConsistencyReviewPanel
          review={consistencyReview}
          busy={consistencyReviewBusy}
          error={consistencyReviewError}
          pendingSubmit={pendingSubmitAfterReview}
          onContinue={() => {
            setPendingSubmitAfterReview(false);
            openVerifyModal();
          }}
          onDismiss={() => {
            setPendingSubmitAfterReview(false);
            setConsistencyReview(null);
            openVerifyModal();
          }}
        />

        {/*
          Gated on docExists (matters/{matterId} has actually been written at least once),
          not on routeId/the URL: firestore.rules distinguishes create vs. update for
          collaboratorUids writes, so sharing has nothing to attach to until that first
          write happens. On a brand-new matter (matters/new) that write comes from the
          debounced autosave a few seconds after the first edit, well before routeId would
          ever reflect it — the URL never updates to point at the new doc.
        */}
        {docExists && sharingEnabled && (
          <SharedWithCard
            matterId={matterId}
            matter={matter}
            roster={roster.data}
            canEdit={isOwner || isAdmin}
            defaultOpen={false}
          />
        )}

        {/* clonePolicyFieldList (not the effective form list): hidden/deleted fields'
            "never copy" flags must keep protecting their saved answers in templates. */}
        <SaveAsTemplate
          matter={matter}
          effectiveFields={clonePolicyFieldList(FORM_SECTIONS, config.fieldLayout)}
          sections={sections}
        />

        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={activeSection === 0}
            onClick={() => setActiveSection((i) => i - 1)}
            className="rounded border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm disabled:opacity-40"
          >
            Back
          </button>
          <button
            type="button"
            disabled={activeSection >= sections.length - 1}
            onClick={() => {
              if (!currentSectionComplete) {
                showNavigationProblems();
                return;
              }
              setActiveSection((i) => i + 1);
            }}
            className="rounded border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm disabled:opacity-40"
          >
            Next
          </button>
          <button
            type="button"
            onClick={() => save(matter)}
            className="rounded border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Save draft
          </button>
          {consistencyReviewEnabled && (
            <button
              type="button"
              onClick={() => runConsistencyReview()}
              disabled={consistencyReviewBusy || submitting}
              className="rounded border border-amber-400 px-4 py-2 text-sm text-amber-900 hover:bg-amber-50 disabled:opacity-50 dark:text-amber-100 dark:hover:bg-amber-950"
            >
              {consistencyReviewBusy ? 'Reviewing…' : 'Review for consistency'}
            </button>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="ml-auto rounded bg-firm-700 dark:bg-firm-500 px-5 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : submitLabel}
          </button>
        </div>
      </div>

      {verifyModalOpen && (
        <VerifyModal
          matterId={matterId}
          fields={fieldsToVerify}
          matter={matter}
          roster={config.roster}
          workflowEnabled={workflowEnabled}
          submitLabel={submitLabel}
          verifiedPaths={verifiedPaths}
          setVerifiedPaths={setVerifiedPaths}
          submitting={submitting}
          onCancel={() => setVerifyModalOpen(false)}
          onConfirm={async () => {
            setVerifyModalOpen(false);
            await doSubmit();
          }}
        />
      )}
    </div>
  );
}

/**
 * Pre-submit verification checklist — every visible field an admin flagged
 * "Verification required" (lib/fieldLayout.js) must be explicitly confirmed before
 * submission proceeds. This is a separate gate from required/soft-required validation:
 * it runs only after `validation.canSubmit` is already true.
 *
 * If the matter changes while the modal is open (e.g. the user edited a field in another
 * tab/render), the checklist is reset rather than trusted stale — see the effect below.
 *
 * Also shows a read-only "Who will approve this" preview (the previewApprovalChain
 * callable), fetched once when the modal opens, so a submitter can see the actual
 * approval routing — same chain-builder logic submitMatter() will use — before
 * committing to submit. The preview never blocks submission: a failure to load it just
 * hides the section (allVerified/onConfirm are unaffected).
 */
function VerifyModal({
  matterId,
  fields,
  matter,
  roster,
  workflowEnabled,
  // The label on the confirm button. It is admin-configurable, so it has to be passed in — this
  // component read it straight out of the enclosing scope, which is not a scope it has, and threw
  // a ReferenceError every time the modal rendered.
  submitLabel,
  verifiedPaths,
  setVerifiedPaths,
  submitting,
  onCancel,
  onConfirm,
}) {
  const matterRef = useRef(matter);
  useEffect(() => {
    if (matterRef.current !== matter) {
      matterRef.current = matter;
      setVerifiedPaths({});
    }
  }, [matter, setVerifiedPaths]);

  const allVerified = fields.every((f) => verifiedPaths[f.path]);

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(true);

  useEffect(() => {
    if (!workflowEnabled) {
      setPreview(null);
      setPreviewError('');
      setPreviewLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError('');
    httpsCallable(functions, 'previewApprovalChain')({ matterId })
      .then((result) => {
        if (!cancelled) setPreview(result.data);
      })
      .catch((err) => {
        if (!cancelled) setPreviewError(err.message || String(err));
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [matterId, workflowEnabled]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60 p-4">
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white dark:bg-slate-900 p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-firm-700 dark:text-firm-100">
          Please verify the following information
        </h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          These fields were flagged as requiring a final check before this matter is
          {workflowEnabled ? 'submitted for approval' : 'submitted'}. Confirm each value is correct.
        </p>

        {workflowEnabled && (
          <div className="mt-4 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-3">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Who will approve this</h3>
          {previewLoading && <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Loading approval routing…</p>}
          {!previewLoading && previewError && (
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Couldn&apos;t load the approval routing preview ({previewError}). You can still submit —
              this preview is informational only.
            </p>
          )}
          {!previewLoading && !previewError && preview && preview.groups.length === 0 && (
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">No approval steps would be created.</p>
          )}
          {!previewLoading && !previewError && preview && preview.groups.length > 0 && (
            <ol className="mt-2 space-y-1.5">
              {preview.groups.map((group, index) => (
                <li key={group.groupIndex} className="text-sm text-slate-700 dark:text-slate-200">
                  <span className="font-medium text-slate-500 dark:text-slate-400">{index + 1}.</span>{' '}
                  {group.steps.map((step, stepIndex) => (
                    <span key={step.key}>
                      {stepIndex > 0 && '; '}
                      {step.label}
                      {': '}
                      {step.unresolved ? (
                        <span className="font-medium text-amber-700 dark:text-amber-300">
                          unresolved — needs admin reassignment
                        </span>
                      ) : (
                        <span className="font-medium">{step.approverNames.join(' or ') || '—'}</span>
                      )}
                    </span>
                  ))}
                  {group.steps.length > 1 && (
                    <span className="ml-1 text-xs text-slate-500 dark:text-slate-400">(sent together — all must approve)</span>
                  )}
                </li>
              ))}
            </ol>
          )}
          </div>
        )}

        {fields.length > 0 && (
          <ul className="mt-4 divide-y divide-slate-100 dark:divide-slate-800">
            {fields.map((field) => {
            const value = getPath(matter, field.path);
            return (
              <li key={field.path} className="flex items-start gap-3 py-3">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={Boolean(verifiedPaths[field.path])}
                  onChange={(e) =>
                    setVerifiedPaths((prev) => ({ ...prev, [field.path]: e.target.checked }))
                  }
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{field.label}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{formatFieldValueForDisplay(field, value, roster)}</p>
                </div>
              </li>
            );
            })}
          </ul>
        )}

        <div className="mt-4 flex items-center gap-2">
          {fields.length > 0 && (
            <button
              type="button"
              onClick={() =>
                setVerifiedPaths(Object.fromEntries(fields.map((f) => [f.path, true])))
              }
              className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Verify all
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!allVerified || submitting}
            onClick={onConfirm}
            className="ml-auto rounded bg-firm-700 dark:bg-firm-500 px-5 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Saves the current answers as a reusable template.
 *
 * stripPreventCloneFields runs HERE, in the browser, before the payload is handed to the
 * callable — a field an admin flagged "prevent clone/template" must never reach the
 * server inside a template, not merely be hidden when the template is applied. The
 * lifecycle keys go too: a template is a starting point, never a submitted matter.
 */
function SaveAsTemplate({ matter, effectiveFields, sections }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [done, setDone] = useState('');
  const { run, busy, error } = useCallable();

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm text-firm-700 dark:text-firm-100 hover:bg-slate-100 dark:hover:bg-slate-800"
      >
        Save as template
      </button>
    );
  }

  const save = async () => {
    const data = stripLifecycleFields(stripPreventCloneFields(matter, effectiveFields));
    const result = await run('saveTemplate', {
      name,
      matterData: data,
      sectionsPreview: sections.map((s) => s.title),
    });
    if (result) {
      setDone(`Saved “${name}” — it is now available under Templates.`);
      setName('');
    }
  };

  return (
    <div className="rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 p-4">
      <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Save as template</h3>
      <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
        Everything you have entered is stored for re-use, minus any field an administrator
        flagged as “never copy” and minus matter numbers, approvals and attachments.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          className="flex-1 rounded border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm"
          placeholder="Template name, e.g. “Standard school district litigation”"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button
          type="button"
          onClick={save}
          disabled={busy || !name.trim()}
          className="rounded bg-firm-700 dark:bg-firm-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
        >
          Save template
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-sm text-slate-500 dark:text-slate-400 hover:underline"
        >
          Cancel
        </button>
      </div>
      {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
      {done && <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">{done}</p>}
    </div>
  );
}

/**
 * Soft-required fields do not block submission, but leaving one blank has to be a
 * deliberate act with a reason attached — the reasons are written to the matter's
 * audit log by the submitMatter callable.
 */
function SoftRequiredPanel({ softRequired, bypasses, setBypasses, reasonRules }) {
  /**
   * Renders from softRequired, which keeps a row after its reason becomes acceptable.
   *
   * It used to render from `warnings`, and a field leaves that list the moment its reason is
   * accepted — at exactly 15 characters by default, which lands mid-word. The row and the box
   * being typed into disappeared underneath the person writing, and whatever fragment they had
   * reached is what got submitted and frozen onto the matter.
   */
  const rows = softRequired.filter((w) => w.level === REQUIREMENT.SOFT_REQUIRED);
  const outstanding = rows.filter((w) => !w.waived).length;
  // Both disclosures default ON when unset, matching submitMatter's `!== false` — the copy
  // must describe what will actually happen, not what an absent setting looks like.
  const inPdf = reasonRules?.includeInPdf !== false;
  const toApprovers = reasonRules?.showToApprovers !== false;
  return (
    <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 p-4">
      <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
        {outstanding === 0
          ? `${rows.length} field${rows.length === 1 ? '' : 's'} explained`
          : `${outstanding} of ${rows.length} field${rows.length === 1 ? '' : 's'} still to explain`}
      </h3>
      <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
        Complete these fields or explain why they are blank before submitting. A reason has to
        be a real explanation — &ldquo;n/a&rdquo; will not be accepted. It is recorded on the
        matter and in its audit log as of the moment you submit, and cannot be edited
        afterwards
        {inPdf ? ', printed on the matter record' : ''}
        {toApprovers ? ', and shown to every approver' : ''}.
      </p>
      <ul className="mt-3 space-y-2">
        {rows
          .map((w) => {
            const reason = bypasses[w.path] || '';
            // Shown only once they have started typing: complaining about a reason nobody
            // has written yet would just be noise next to the warning itself.
            const problem = reason.trim() ? bypassReasonProblem(reason, reasonRules) : '';
            return (
              <li key={w.path} className="flex flex-col gap-1">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center">
                  <span className="flex w-72 shrink-0 items-center gap-1.5 text-sm text-amber-900 dark:text-amber-100">
                    {/* A tick rather than removal: the row has to stay put while it is being typed in. */}
                    <span
                      aria-hidden="true"
                      className={`text-xs ${w.waived ? 'text-accent-700 dark:text-accent-500' : 'text-transparent'}`}
                    >
                      ✓
                    </span>
                    {w.label}
                  </span>
                  <input
                    className={`flex-1 rounded border px-3 py-1.5 text-sm ${
                      problem
                        ? 'border-red-400 dark:border-red-600'
                        : 'border-amber-300 dark:border-amber-700'
                    }`}
                    placeholder="Reason this is blank…"
                    aria-invalid={problem ? 'true' : undefined}
                    value={reason}
                    onChange={(e) => setBypasses((prev) => ({ ...prev, [w.path]: e.target.value }))}
                  />
                </div>
                {problem && (
                  <p className="text-xs text-red-700 dark:text-red-300 sm:ml-72 sm:pl-2">{problem}</p>
                )}
              </li>
            );
          })}
      </ul>
    </div>
  );
}
